/**
 * Game formulas. The server is the only one that acts on these; the client imports them
 * so its tooltips and buttons agree with what the server will decide.
 */
import { BASE_TILES_PER_TICK } from './constants.js';
import type { CharacterState } from './character.js';
import { RESOURCES, type ResourceDef } from './content/gathering.js';
import { ITEMS, LOGS, type ItemId } from './content/items.js';
import { levelOf, SKILL_NAMES, type SkillId } from './content/skills.js';
import type { Ingredient, RecipeDef } from './content/crafting.js';
import { LINE_INFO, SUPPLIES, TOOLS, TOOL_IDS, isToolId, toolDef, type SupplyId, type ToolId, type ToolLine } from './content/tools.js';
import { sellersOf } from './content/shops.js';

/** A skill's level, with any potion boost on top. */
export const level = (c: Pick<CharacterState, 'xp'> & { boosts?: CharacterState['boosts'] }, s: SkillId) => levelOf(c.xp[s]) + (c.boosts?.[s]?.[0] ?? 0);
/** A skill's level without boosts (for xp and level-ups). */
export const baseLevel = (c: Pick<CharacterState, 'xp'>, s: SkillId) => levelOf(c.xp[s]);

/* ---------------- tools ---------------- */
type Carrier = Pick<CharacterState, 'inv' | 'xp'> & { equip?: CharacterState['equip'] };
/** Tools are pack items: you have a tool when it is in your pack, or in your hand (a hatchet you fight with still chops). */
const inHand = (c: { equip?: CharacterState['equip'] }) => (c.equip?.weapon ?? null) as ItemId | null;
const toolsOn = (c: Pick<CharacterState, 'inv'> & { equip?: CharacterState['equip'] }) => { const w = inHand(c); return w ? [...c.inv, w] : c.inv; };
export const carries = (c: Pick<CharacterState, 'inv'> & { equip?: CharacterState['equip'] }, id: ItemId) => toolsOn(c).includes(id);
/** Has this tool at all, in the pack or the chest. */
export const owns = (c: Pick<CharacterState, 'inv' | 'bank'>, id: ItemId) => c.inv.includes(id) || (c.bank[id] ?? 0) > 0;
export const canUse = (c: Pick<CharacterState, 'xp'>, id: ToolId) => !toolDef(id).broken && level(c, toolDef(id).use[0]) >= toolDef(id).use[1];
/** Better of two tools in a line: higher tier, then more power (a fine pick beats a plain one). */
const better = (a: ToolId, b: ToolId | null) => !b || TOOLS[a].tier > TOOLS[b].tier || (TOOLS[a].tier === TOOLS[b].tier && TOOLS[a].power > TOOLS[b].power);
/** Best tool of a line in the pack that this character can use, or null. */
export function bestTool(c: Carrier, line: ToolLine): ToolId | null {
  let best: ToolId | null = null;
  for (const id of toolsOn(c)) { if (!id || !isToolId(id)) continue; const d = TOOLS[id]; if (d.line === line && canUse(c, id) && better(id, best)) best = id; }
  return best;
}
/** Best tool of a line in the pack, usable or not. */
export function bestOwned(c: Pick<CharacterState, 'inv'> & { equip?: CharacterState['equip'] }, line: ToolLine): ToolId | null {
  let best: ToolId | null = null;
  for (const id of toolsOn(c)) { if (!id || !isToolId(id)) continue; const d = TOOLS[id]; if (d.line === line && (!best || d.tier > TOOLS[best].tier)) best = id; }
  return best;
}
/** Best tool of a line in the chest. */
export function bestStored(c: Pick<CharacterState, 'bank'>, line: ToolLine): ToolId | null {
  let best: ToolId | null = null;
  for (const id of TOOL_IDS) if ((c.bank[id] ?? 0) > 0 && TOOLS[id].line === line && (!best || TOOLS[id].tier > TOOLS[best].tier)) best = id;
  return best;
}
/** The tool in `line` at `tier` (for messages like "an iron hatchet or better"). */
export const toolAtTier = (line: ToolLine, tier: number) => TOOL_IDS.find(id => TOOLS[id].line === line && TOOLS[id].tier === tier) ?? null;
/** Lowest-tier tool in a line that a shop sells (else the lowest tier). */
function cheapestIn(line: ToolLine): ToolId | null {
  const ids = TOOL_IDS.filter(id => TOOLS[id].line === line).sort((a, b) => TOOLS[a].tier - TOOLS[b].tier);
  return ids.find(id => sellersOf(id)) ?? ids[0] ?? null;
}
const plural = (s: string) => /s$/.test(s.replace(' or better', ''));
const an = (s: string) => (plural(s) ? s : /^[aeiou]/i.test(s) ? `an ${s}` : `a ${s}`);
const where = (id: ToolId | null) => {
  if (!id) return '';
  const who = sellersOf(id), them = plural(TOOLS[id].name);
  return who ? ` ${who} sells ${them ? 'them' : 'one'}.` : ` Smith ${them ? 'them' : 'one'} at an anvil.`;
};

/** Why a requirement isn't met: a sentence for the log and a few words for tooltips. */
export interface Blocker { text: string; short: string }

/** Checks that a usable tool of `line`, tier `min` or better, is in the pack. */
export function toolBlocker(c: CharacterState, line: ToolLine, min = 0, purpose = ''): Blocker | null {
  const use = bestTool(c, line), own = bestOwned(c, line);
  if (use && TOOLS[use].tier >= min) return null;
  const lineName = LINE_INFO[line].name;
  if (own && TOOLS[own].broken && TOOLS[own].tier >= min) return { text: `Your ${TOOLS[own].name.replace(' (broken)', '').toLowerCase()} is broken. Bram can repair it, or repair it yourself at an anvil.`, short: `repair your ${LINE_INFO[line].name}` };
  if (own && TOOLS[own].tier >= min) {
    const [sk, lv] = toolDef(own).use;
    return { text: `You need ${SKILL_NAMES[sk]} ${lv} to use your ${TOOLS[own].name.toLowerCase()}.`, short: `${SKILL_NAMES[sk]} ${lv}` };
  }
  const stored = bestStored(c, line);
  if (stored && TOOLS[stored].tier >= min && canUse(c, stored)) {
    return { text: `Your ${TOOLS[stored].name.toLowerCase()} is in the chest. Take it out to use it${purpose ? ` ${purpose}` : ''}.`, short: `${TOOLS[stored].name.toLowerCase()} (in chest)` };
  }
  const want = min > 0 ? toolAtTier(line, min) : cheapestIn(line);
  const top = TOOL_IDS.reduce((m, id) => TOOLS[id].line === line ? Math.max(m, TOOLS[id].tier) : m, 0);
  const name = want ? `${TOOLS[want].name.toLowerCase()}${min > 0 && want !== toolAtTier(line, top) ? ' or better' : ''}` : lineName;
  return { text: `You need ${an(name)}${purpose ? ` ${purpose}` : ''}.${where(want)}`, short: name.replace(' or better', '+') };
}

/** Checks a supply (bait, feathers) is in the pack. */
export function supplyBlocker(c: CharacterState, id: SupplyId): Blocker | null {
  if (invCount(c, id) > 0) return null;
  const sup = SUPPLIES[id], name = sup.name.toLowerCase();
  if ((c.bank[id] ?? 0) > 0) return { text: `Your ${name} is in the chest.`, short: `${name} (in chest)` };
  return { text: `You have no ${name}. ${sellersOf(id)} sells ${name}.`, short: name };
}

/** Bonus the best usable tool gives to gathering with `line`. */
export const toolPower = (c: CharacterState, line: ToolLine) => { const t = bestTool(c, line); return t ? TOOLS[t].power : 0; };

export function gatherChance(c: CharacterState, def: ResourceDef): number {
  const food = (def.skill === 'mining' && c.buffs?.delver) || (def.skill === 'forestry' && c.buffs?.woodsman) ? 0.05 : 0;
  const base = 0.3 + (level(c, def.skill) - def.level) * 0.025 + (def.tool ? toolPower(c, def.tool) : 0.2) + food;
  const fed = c.fedTicks > 0 ? 1.15 : 1;
  return Math.min(0.95, Math.max(0.12, base * fed));
}

/**
 * Burning, the RuneScape way: at a recipe's level about half of it burns, and the chance falls
 * steadily to nothing at its stop-burn level (the recipe's level + 30 at a fire by default). A
 * range stops you burning 5 levels sooner, cook's gauntlets 6 sooner, and a master chef (99)
 * never burns anything. `oven`: cooking at a range or clay oven.
 */
export function burnChance(c: CharacterState, r: RecipeDef, oven = false): number {
  const L = level(c, 'cooking');
  if (L >= 99 || !r.burns) return 0;
  const stop = (r.stopBurn ?? r.level + 30) - (oven ? 5 : 0) - (toolPower(c, 'gauntlets') > 0 ? 6 : 0);
  if (L >= stop) return 0;
  return Math.min(0.6, 0.5 * (stop - L) / Math.max(1, stop - r.level));
}
/** Well past a recipe, a cook sometimes turns out a hearty batch: two for the price of one. */
export const heartyChance = (c: Pick<CharacterState, 'xp'>, r: RecipeDef) => { const over = level(c, 'cooking') - r.level - 10; return over <= 0 ? 0 : Math.min(0.3, over * 0.02); };

export function tilesPerTick(c: CharacterState): number {
  const ex = level(c, 'exploration');
  return BASE_TILES_PER_TICK * (1 + ex * 0.005) * (ex >= 30 ? 1.15 : 1) * (c.upgrades.boots ? 1.15 : 1) * (c.fedTicks > 0 ? 1.08 : 1) * (c.buffs?.swift ? 1.1 : 1);
}

export const revealRadius = (c: CharacterState, base: number, lantern: number) => (c.upgrades.lantern ? lantern : base);

/** Why this character can't gather from `kind`, or null if they can (ignoring pack space). */
export function gatherBlocker(c: CharacterState, kind: keyof typeof RESOURCES): Blocker | null {
  const d = RESOURCES[kind];
  if (level(c, d.skill) < d.level) return { text: `You need ${SKILL_NAMES[d.skill]} ${d.level} to ${d.verb.toLowerCase()} ${d.noun}.`, short: `${SKILL_NAMES[d.skill]} ${d.level}` };
  const tool = d.tool ? toolBlocker(c, d.tool, d.minTier ?? 0, `for ${d.noun}`) : null;
  if (tool) return tool;
  return d.uses ? supplyBlocker(c, d.uses) : null;
}

/** Why this character can't make `r` right now (level and tools; not materials). */
export function craftBlocker(c: CharacterState, r: RecipeDef): Blocker | null {
  if (level(c, r.skill) < r.level) return { text: `You need ${SKILL_NAMES[r.skill]} ${r.level} for that.`, short: `${SKILL_NAMES[r.skill]} ${r.level}` };
  for (const need of r.needs ?? []) { const b = toolBlocker(c, TOOLS[need].line, TOOLS[need].tier, `to make ${r.name.toLowerCase()}`); if (b) return b; }
  return null;
}

/** True when the pack already holds this tool or a better one of its line. */
export function hasToolOrBetter(c: Pick<CharacterState, 'inv'> & { equip?: CharacterState['equip'] }, id: ToolId) {
  const d = TOOLS[id];
  return toolsOn(c).some(t => !!t && isToolId(t) && TOOLS[t].line === d.line && TOOLS[t].tier >= d.tier);
}

/* ---- inventory helpers (pure; the server applies them to the real state) ----
 * A stackable item occupies one slot and keeps its count in `c.stacks`. */
export const isStackable = (id: ItemId) => !!ITEMS[id].stack;
export const invFree = (c: Pick<CharacterState, 'inv'>) => c.inv.reduce((n, v) => n + (v ? 0 : 1), 0);
export function invCount(c: Pick<CharacterState, 'inv' | 'stacks'>, id: Ingredient): number {
  if (id === 'LOG') return LOGS.reduce((a, l) => a + invCount(c, l), 0);
  if (isStackable(id)) return c.inv.includes(id) ? c.stacks[id] ?? 0 : 0;
  return c.inv.reduce((n, v) => n + (v === id ? 1 : 0), 0);
}
/** Can `n` of `id` go into the pack? */
export function invRoom(c: Pick<CharacterState, 'inv'>, id: ItemId, n = 1): boolean {
  if (isStackable(id)) return c.inv.includes(id) || invFree(c) > 0;
  return invFree(c) >= n;
}
/* ---- carriers: the ore sack, the log basket and the fish barrel ---- */
/**
 * Things you carry that hold one kind of haul so your pack stays free: ore in a sack, logs in a
 * basket, raw fish in a barrel. Their contents all live in `c.sack` (an item → count map); each
 * carrier only counts and takes its own kind. Furnaces, benches and fires draw from them too.
 */
export const CARRIERS = [
  { line: 'sack', name: 'Ore sack', cat: 'ore', noun: 'ore' },
  { line: 'basket', name: 'Log basket', cat: 'log', noun: 'logs' },
  { line: 'barrel', name: 'Fish barrel', cat: 'raw', noun: 'fish' },
] as const;
export type CarrierLine = (typeof CARRIERS)[number]['line'];
/** Which carrier takes this item, if any. */
export const carrierOf = (id: ItemId) => CARRIERS.find(k => ITEMS[id]?.cat === k.cat) ?? null;
/** How much the best carrier of a line holds (0 without one). */
export function carrierHolds(c: Carrier, line: CarrierLine) { const t = bestTool(c, line); return t ? TOOLS[t].holds ?? 0 : 0; }
/** How much is in a carrier now. */
export function carrierTotal(c: Pick<CharacterState, 'sack'>, line: CarrierLine) {
  const k = CARRIERS.find(x => x.line === line)!;
  return Object.entries(c.sack ?? {}).reduce((a, [id, n]) => a + (ITEMS[id as ItemId]?.cat === k.cat ? n ?? 0 : 0), 0);
}
/** Is there a carrier with room for one of this? */
export function carrierRoom(c: CharacterState, id: ItemId) { const k = carrierOf(id); return !!k && carrierHolds(c, k.line) > carrierTotal(c, k.line); }
/** Put one into its carrier if there's room. */
export function carrierAdd(c: CharacterState, id: ItemId) { if (!carrierRoom(c, id)) return false; c.sack[id] = (c.sack[id] ?? 0) + 1; return true; }
/** How much ore this character's best sack holds (0 without one). */
export function sackHolds(c: Carrier) { return carrierHolds(c, 'sack'); }
export const sackTotal = (c: Pick<CharacterState, 'sack'>) => carrierTotal(c, 'sack');
/** Room for one more of this haul, in its carrier or the pack. */
export const canTake = (c: CharacterState, id: ItemId) => carrierRoom(c, id) || invRoom(c, id);
/** Room for how many more of this haul, carrier and pack together. */
export function takeRoom(c: CharacterState, id: ItemId) { const k = carrierOf(id); return (k ? Math.max(0, carrierHolds(c, k.line) - carrierTotal(c, k.line)) : 0) + (isStackable(id) ? (invRoom(c, id) ? 9999 : 0) : invFree(c)); }
/** Stow `n` of a haul: into its carrier while there's room, then the pack. Returns how many went in. */
export function stow(c: CharacterState, id: ItemId, n = 1) { let k = 0; while (k < n && carrierAdd(c, id)) k++; if (k < n && invAdd(c, id, n - k)) k = n; return k; }
const inCarrier = (c: CharacterState, id: ItemId) => (carrierOf(id) ? c.sack?.[id] ?? 0 : 0);
/** Ingredient counts from the pack and the carriers together (furnaces, benches and fires draw from both). */
export function craftCount(c: CharacterState, id: Ingredient): number {
  if (id === 'LOG') return invCount(c, 'LOG') + LOGS.reduce((a, l) => a + inCarrier(c, l), 0);
  return invCount(c, id) + inCarrier(c, id);
}
/** Take ingredients, from the carriers first (any log: the cheapest first). */
export function craftRemove(c: CharacterState, id: Ingredient, n: number): boolean {
  if (craftCount(c, id) < n) return false;
  const fromCarrier = (item: ItemId) => {
    const take = Math.min(n, inCarrier(c, item));
    if (!take) return;
    const left = (c.sack[item] ?? 0) - take;
    if (left > 0) c.sack[item] = left; else delete c.sack[item];
    n -= take;
  };
  if (id === 'LOG') { for (const l of LOGS) { if (!n) break; fromCarrier(l); } }
  else fromCarrier(id);
  return n === 0 || invRemove(c, id, n);
}
export function maxCraftable(c: CharacterState, r: RecipeDef): number {
  let m = Infinity;
  for (const [k, n] of Object.entries(r.inputs) as [Ingredient, number][]) m = Math.min(m, Math.floor(craftCount(c, k) / n));
  return m === Infinity ? 0 : m;
}
/** Remove `n` of an ingredient. Returns false (and changes nothing) if there aren't enough. */
export function invRemove(c: CharacterState, id: Ingredient, n: number): boolean {
  if (invCount(c, id) < n) return false;
  if (id === 'LOG') {
    let left = n;
    for (const l of LOGS) { const take = Math.min(left, invCount(c, l)); invRemove(c, l, take); left -= take; if (!left) break; }
    return true;
  }
  if (isStackable(id)) {
    const left = (c.stacks[id] ?? 0) - n;
    if (left > 0) c.stacks[id] = left; else { delete c.stacks[id]; c.inv[c.inv.indexOf(id)] = null; }
    return true;
  }
  let left = n;
  for (let i = c.inv.length - 1; i >= 0 && left; i--) if (c.inv[i] === id) { c.inv[i] = null; left--; }
  return true;
}
/** Add `n` of an item (all or nothing). */
export function invAdd(c: CharacterState, id: ItemId, n = 1): boolean {
  if (!invRoom(c, id, n)) return false;
  if (isStackable(id)) {
    if (!c.inv.includes(id)) c.inv[c.inv.indexOf(null)] = id;
    c.stacks[id] = (c.stacks[id] ?? 0) + n;
    return true;
  }
  for (let k = 0; k < n; k++) c.inv[c.inv.indexOf(null)] = id;
  return true;
}
/** Empty one slot, returning what was in it and how many. */
export function invTake(c: CharacterState, slot: number): [ItemId, number] | null {
  const id = c.inv[slot];
  if (!id) return null;
  c.inv[slot] = null;
  if (isStackable(id)) { const n = c.stacks[id] ?? 0; delete c.stacks[id]; return [id, n]; }
  return [id, 1];
}
export function invCompact(c: CharacterState) {
  const items = c.inv.filter(Boolean);
  c.inv = [...items, ...new Array(c.inv.length - items.length).fill(null)];
}
